import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  X,
  Info,
  UsersRound,
  UserCog,
  Search,
  UserPlus,
  ShieldCheck,
} from 'lucide-react';
import '../CSS/GuideModal.css';

const StaffsGuideModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating Action Button ── */}
      <div className="gml-fab-wrapper">
        <span className="gml-fab-tooltip">Need help?</span>
        <button
          className="gml-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open Staff Guide"
          title="Staff Management Guide"
        >
          <BookOpen className="gml-fab-icon" />
        </button>
      </div>

      {/* ── Modal ── */}
      {isOpen && (
        <div
          className="gml-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gml-modal-title"
        >
          <div className="gml-modal">

            {/* Header */}
            <div className="gml-modal-header">
              <div className="gml-modal-title-wrap">
                <BookOpen className="gml-modal-header-icon" />
                <h2 className="gml-modal-title" id="gml-modal-title">
                  Staffs Module Guide
                </h2>
              </div>
              <button
                className="gml-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close guide"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="gml-modal-body">

              {/* Welcome Banner */}
              <div className="gml-welcome-banner">
                <Info className="gml-welcome-icon" />
                <div>
                  <p className="gml-welcome-heading">
                    Welcome to the Staffs Module!
                  </p>
                  <p className="gml-welcome-text">
                    This guide will help you effectively manage all staff members
                    in your institution. Use it anytime you need a quick reference.
                  </p>
                </div>
              </div>

              {/* ── Key Features Overview ── */}
              <h3 className="gml-section-heading">Key Features Overview</h3>
              <div className="gml-cards-grid">

                {/* Staff Records */}
                <div className="gml-feature-card gml-feature-card--blue">
                  <div className="gml-card-head gml-card-head--blue">
                    <UsersRound className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Staff Records</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">1</span>
                        View all staff members with complete details
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">2</span>
                        Search staff by name with instant results
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">3</span>
                        Edit staff information with one click
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Staff Operations */}
                <div className="gml-feature-card gml-feature-card--green">
                  <div className="gml-card-head gml-card-head--green">
                    <UserCog className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Staff Operations</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">1</span>
                        Add new staff with complete profile information
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">2</span>
                        Update existing staff records
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">3</span>
                        Remove staff members with confirmation
                      </li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Detailed Feature Guide ── */}
              <h3 className="gml-section-heading">Detailed Feature Guide</h3>
              <div className="gml-detail-section">

                {/* Managing Staff Records */}
                <div className="gml-detail-card gml-detail-card--cyan">
                  <div className="gml-detail-head gml-detail-head--cyan">
                    <Search className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Managing Staff Records</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--cyan">Search Functionality</p>
                    <ul className="gml-detail-list">
                      <li>Type in the search box to filter staff by name</li>
                      <li>Search is case-insensitive and works with partial matches</li>
                      <li>Clear the search box to view all staff members</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Staff Actions</p>
                    <ul className="gml-detail-list">
                      <li>Click the edit (pencil) button to modify staff details</li>
                      <li>Click the delete (trash) button to remove a staff record</li>
                      <li>Editing a staff member auto-fills the form for quick updates</li>
                    </ul>
                  </div>
                </div>

                {/* Adding and Editing Staff */}
                <div className="gml-detail-card gml-detail-card--yellow">
                  <div className="gml-detail-head gml-detail-head--yellow">
                    <UserPlus className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Adding and Editing Staff</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--yellow">Required Fields</p>
                    <ul className="gml-detail-list">
                      <li><strong>Name:</strong> Must be a full name (first and last) with letters only</li>
                      <li><strong>Position:</strong> Must contain only letters and spaces</li>
                      <li><strong>Email:</strong> Must be a valid email format</li>
                      <li><strong>Phone:</strong> Must be at least 8 digits (numbers only)</li>
                      <li><strong>Password:</strong> Must be at least 8 characters</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Form Validation</p>
                    <ul className="gml-detail-list">
                      <li>Real-time validation as you type</li>
                      <li>Clear error messages for invalid inputs</li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--muted">Duplicate check</span>
                        — Duplicate email and name detection enforced
                      </li>
                      <li>Password requirements enforced on submission</li>
                    </ul>
                  </div>
                </div>

                {/* Security Features */}
                <div className="gml-detail-card gml-detail-card--green">
                  <div className="gml-detail-head gml-detail-head--green">
                    <ShieldCheck className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Security Features</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--green">Password Handling</p>
                    <ul className="gml-detail-list">
                      <li>Passwords are masked during entry</li>
                      <li>Minimum 8-character requirement enforced</li>
                      <li>Passwords are securely stored in the database</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Delete Protection</p>
                    <ul className="gml-detail-list">
                      <li>Confirmation dialog appears before any deletion</li>
                      <li>Prevents accidental data loss</li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--success">Safe</span>
                        — Clear warning shown before permanent removal
                      </li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Data Integrity</p>
                    <ul className="gml-detail-list">
                      <li>Form validation prevents invalid data entry</li>
                      <li>Consistent data formatting enforced throughout</li>
                      <li>Immediate feedback on errors</li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Best Practices ── */}
              <div className="gml-best-practices">
                <p className="gml-best-practices-heading">Best Practices</p>
                <ul className="gml-best-practices-list">
                  {[
                    'Keep staff records up-to-date with current information.',
                    'Use strong, unique passwords for each staff member.',
                    'Update records promptly when staff positions change.',
                    'Remove departed staff members to maintain accurate records.',
                    'Use the search function to quickly locate specific staff.',
                   'Access this guide anytime via the floating button on the bottom-right of the screen.',
                  ].map((tip, i) => (
                    <li key={i}>
                      <span className="gml-bp-dot" aria-hidden="true" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="gml-modal-footer">
              <button className="gml-close-btn" onClick={() => setIsOpen(false)}>
                Got it
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default StaffsGuideModal;